import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Mail, MapPin, Globe, Sparkles, Brain, Cpu, Zap, Menu, ChevronDown, Check, AlertCircle, Bot, Network, BrainCircuit, Code2, TrendingUp, Clock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { seoConfig, structuredData, localBusinessSchema, faqSchema } from './seo';
import { validateEmail, validatePhone, validateName, validateMessage, countryCodes, formatPhoneNumber } from './utils/validation';
import { initEmailJS, sendContactEmail, sendChatbotEmail } from './utils/emailService';
import ValidatedInput from './components/ValidatedInput';

const translations = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      about: "About",
      contact: "Contact",
      faq: "FAQ"
    },
    hero: {
      title: "Transform Your Business with",
      highlight: "AI Innovation",
      subtitle: "Gulf Neuron specializes in developing cutting-edge AI tools, intelligent applications, and custom machine learning solutions that drive your business forward.",
      cta: "Get Started",
      ctaSecondary: "Our Services"
    },
    services: {
      title: "Our Services",
      subtitle: "Comprehensive AI Solutions Tailored to Your Needs",
      items: [
        {
          title: "AI Chat Applications & Agents",
          desc: "Intelligent conversational AI systems with RAG capabilities, fine-tuning, and advanced automation for seamless customer interactions."
        },
        {
          title: "Custom AI Integration",
          desc: "Seamlessly integrate AI capabilities into your existing websites, systems, and workflows to enhance functionality and user experience."
        },
        {
          title: "Machine Learning Models",
          desc: "Predictive analytics, classification systems, and custom ML models designed to solve your specific business challenges."
        },
        {
          title: "Generative AI Solutions",
          desc: "Text, image, and audio generation. Speech-to-text, text-to-speech, and multimodal AI systems for creative and practical applications."
        },
        {
          title: "AI Tool Development",
          desc: "Full-stack AI application development from concept to deployment, including backend infrastructure and responsive frontends."
        },
        {
          title: "Consulting & Strategy",
          desc: "Expert guidance on AI adoption, technology selection, and implementation strategies to maximize your ROI."
        }
      ]
    },
    about: {
      title: "About Gulf Neuron",
      subtitle: "Your Partner in AI Innovation",
      content: "Based in Muscat, Oman, Gulf Neuron is a premier AI solutions provider serving clients locally and internationally. We specialize in developing sophisticated AI tools, applications, and machine learning systems that transform business operations. Our expertise spans the entire AI development lifecycle - from intelligent chatbots and RAG systems to generative AI and predictive analytics. We deliver production-ready solutions with complete backend infrastructure, modern frontends, and seamless deployment.",
      mission: "Our Mission",
      missionText: "To empower individuals and businesses with cutting-edge AI technology, making advanced artificial intelligence accessible and practical for organizations of all sizes.",
      stats: [
        { number: "100+", label: "AI Models Deployed" },
        { number: "50+", label: "Happy Clients" },
        { number: "24/7", label: "Support Available" }
      ]
    },
    contact: {
      title: "Let's Build Something Amazing",
      subtitle: "Get in touch to discuss your AI project",
      form: {
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email Address",
        phone: "Phone Number",
        message: "Project Details",
        send: "Send Message",
        sending: "Sending...",
        success: "Message sent successfully! We'll get back to you soon.",
        error: "Failed to send message. Please try again or email us directly."
      },
      info: {
        title: "Contact Information",
        email: "Email Us",
        location: "Location",
        locationText: "Al Khuwair, Muscat, Oman",
        hours: "Business Hours",
        hoursText: "Sun - Thu: 9:00 AM - 6:00 PM"
      }
    },
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about our services",
      items: [
        {
          q: "What types of AI solutions do you develop?",
          a: "We develop a wide range of AI solutions including conversational AI, RAG systems, machine learning models, generative AI applications, predictive analytics, and custom AI integrations for existing systems."
        },
        {
          q: "Do you serve international clients?",
          a: "Yes! While we're based in Muscat, Oman, we serve clients globally. We work remotely with businesses worldwide and provide the same high-quality service regardless of location."
        },
        {
          q: "How long does a typical AI project take?",
          a: "Project timelines are tailored to the unique scope and requirements of each project. During our initial consultation, we will assess your needs and provide a detailed roadmap with a clear estimated timeline."
        },
        {
          q: "Do you provide ongoing support after deployment?",
          a: "Absolutely! We offer comprehensive post-deployment support including monitoring, maintenance, updates, and optimization to ensure your AI solutions continue performing optimally."
        },
        {
          q: "What industries do you work with?",
          a: "We work across various industries including e-commerce, healthcare, finance, education, logistics, and more. Our AI solutions are customized to meet the specific needs of each sector."
        },
        {
          q: "Can you integrate AI into our existing systems?",
          a: "Yes! We specialize in seamlessly integrating AI capabilities into existing websites, applications, and business systems without disrupting your current operations."
        }
      ]
    },
    footer: {
      tagline: "Building the Future with Artificial Intelligence",
      rights: "All rights reserved.",
      quickLinks: "Quick Links"
    },
    chat: {
      title: "Chat with Us",
      subtitle: "We're here to help!",
      placeholder: "Type your message...",
      welcome: "Hello! How can we help you with your AI project today?",
      offline: "We're currently offline, but leave us a message and we'll get back to you soon!"
    }
  },

  ar: {
    nav: {
      home: "الرئيسية",
      services: "خدماتنا",
      about: "من نحن",
      contact: "تواصل معنا",
      faq: "الأسئلة الشائعة"
    },
    hero: {
      title: "حول أعمالك باستخدام",
      highlight: "ابتكارات وحلول الذكاء الاصطناعي",
      subtitle: "في جولف نيورون، نطور أدوات الذكاء الاصطناعي والتطبيقات الذكية وحلول التعلم الآلي المصممة خصيصاً لدفع عجلة نمو أعمالك وتحقيق الريادة.",
      cta: "ابدأ رحلتك",
      ctaSecondary: "استكشف خدماتنا"
    },
    services: {
      title: "مجالات خبرتنا",
      subtitle: "حلول ذكية متكاملة تواكب تطلعاتك",
      items: [
        {
          title: "أنظمة المحادثة والوكلاء الأذكياء",
          desc: "نطور أنظمة محادثة متقدمة تعتمد على تقنيات RAG والأتمتة الدقيقة، لتوفير تجربة تفاعلية سلسة واستثنائية لعملائك."
        },
        {
          title: "دمج الذكاء الاصطناعي",
          desc: "نمكّنك من دمج قدرات الذكاء الاصطناعي بسلاسة تامة في منصاتك وأنظمتك الحالية، لتعزيز الكفاءة التشغيلية وتحسين تجربة المستخدم."
        },
        {
          title: "نماذج التعلم الآلي",
          desc: "نقدم حلولاً تحليلية تنبؤية ونماذج تصنيف ذكية مصممة خصيصاً لمعالجة التحديات المعقدة وفتح آفاق جديدة لعملك."
        },
        {
          title: "الذكاء الاصطناعي التوليدي",
          desc: "حلول متعدده تشمل توليد النصوص والصور والأصوات، وتقنيات تحويل الكلام، لتطبيقات عملية ومبتكرة تخدم أهدافك."
        },
        {
          title: "تطوير أدوات الذكاء الاصطناعي",
          desc: "نرافقك في رحلة تطوير شاملة من الفكرة إلى التنفيذ، مع بنية تحتية قوية وواجهات مستخدم عصرية وسريعة الاستجابة."
        },
        {
          title: "الاستشارات الاستراتيجية",
          desc: "نقدم لك المشورة السديدة لتبني تقنيات الذكاء الاصطناعي الأنسب، ونضع استراتيجيات تنفيذ تضمن لك تحقيق أقصى عائد على الاستثمار."
        }
      ]
    },
    about: {
      title: "عن جولف نيورون",
      subtitle: "شريكك الاستراتيجي في الابتكار",
      content: "انطلاقاً من مسقط، سلطنة عمان، تقدم جولف نيورون خدماتها الرائدة في مجال الذكاء الاصطناعي لعملائها محلياً ودولياً. نحن شغوفون بتطوير أدوات ذكية وأنظمة تعلم آلي تحدث نقلة نوعية في آليات العمل. تغطي خبراتنا دورة التطوير بالكامل، بدءاً من روبوتات الدردشة المتقدمة وصولاً إلى التحليلات التنبؤية، لنقدم لك حلولاً تقنية متكاملة وجاهزة للمستقبل.",
      mission: "رسالتنا",
      missionText: "نسعى لتمكين الافراد والمؤسسات من خلال تقنيات الذكاء الاصطناعي المتقدمة، وجعل هذه التكنولوجيا الثورية في متناول الجميع لخدمة أهداف التطوير والنمو.",
      stats: [
        { number: "100+", label: "مشروع ناجح" },
        { number: "50+", label: "شريك نجاح" },
        { number: "24/7", label: "دعم فني" }
      ]
    },
    contact: {
      title: "لنبني المستقبل معاً",
      subtitle: "يسعدنا الاستماع إلى أفكارك ومناقشة مشروعك",
      form: {
        firstName: "الاسم الأول",
        lastName: "الاسم الأخير",
        email: "البريد الإلكتروني",
        phone: "رقم الهاتف",
        message: "تفاصيل المشروع",
        send: "إرسال الطلب",
        sending: "جاري الإرسال...",
        success: "تم استلام رسالتك بنجاح! فريقنا سيتواصل معك قريباً.",
        error: "نعتذر، حدث خطأ أثناء الإرسال. يرجى المحاولة لاحقاً أو التواصل المباشر."
      },
      info: {
        title: "بيانات التواصل",
        email: "البريد الإلكتروني",
        location: "موقعنا",
        locationText: "الخوير، مسقط، سلطنة عمان",
        hours: "أوقات العمل",
        hoursText: "الأحد - الخميس: 9:00 ص - 6:00 م"
      }
    },
    faq: {
      title: "تساؤلات شائعة",
      subtitle: "إجابات سريعة حول خدماتنا وحلولنا",
      items: [
        {
          q: "ما هي طبيعة حلول الذكاء الاصطناعي التي تقدمونها؟",
          a: "نقدم باقة شاملة من الحلول التقنية، بدءاً من أنظمة المحادثة الذكية وتقنيات RAG، وصولاً إلى نماذج التعلم الآلي والتحليلات التنبؤية، وكل ما يلزم لدمج الذكاء الاصطناعي في بيئة عملك."
        },
        {
          q: "هل تقدمون خدماتكم خارج سلطنة عمان؟",
          a: "بكل تأكيد! رغم أن مقرنا الرئيسي في مسقط، إلا أننا نعمل مع شركاء وعملاء حول العالم عن بُعد، ونلتزم بتقديم أعلى معايير الجودة والاحترافية أينما كنت."
        },
        {
          q: "ما هي المدة المتوقعة لإنجاز مشروع ذكاء اصطناعي؟",
          a: "يختلف الجدول الزمني باختلاف طبيعة المشروع وحجمه. في جلستنا الاستشارية الأولى، سنقوم بتحليل متطلباتك بدقة لنزودك بخطة عمل واضحة وجدول زمني مدروس."
        },
        {
          q: "هل توفرون خدمات الدعم والصيانة بعد التسليم؟",
          a: "نعم، علاقتنا بعملائنا لا تنتهي بالتسليم؛ نحن نوفر دعماً فنياً مستمراً يشمل المراقبة والتحديث والتحسين لضمان استمرارية وكفاءة الحلول المقدمة."
        },
        {
          q: "ما هي القطاعات التي تخدمونها؟",
          a: "تمتد خدماتنا لتشمل قطاعات متنوعة مثل التجارة الإلكترونية، الرعاية الصحية، القطاع المالي، التعليم، واللوجستيات. حلولنا مصممة بمرونة لتلائم متطلبات كل قطاع."
        },
        {
          q: "لدينا أنظمة قائمة، هل يمكنكم دمج الذكاء الاصطناعي بها؟",
          a: "بالتأكيد. أحد أهم اختصاصاتنا هو الدمج التقني السلس، حيث نقوم بإضافة قدرات الذكاء الاصطناعي إلى أنظمتكم الحالية دون التأثير على سير العمليات اليومية."
        }
      ]
    },
    footer: {
      tagline: "نبتكر المستقبل بقوة الذكاء الاصطناعي",
      rights: "جميع الحقوق محفوظة.",
      quickLinks: "روابط سريعة"
    },
    chat: {
      title: "مساعدك الذكي",
      subtitle: "نحن هنا لخدمتك",
      placeholder: "اكتب رسالتك هنا...",
      welcome: "مرحباً بك! كيف يمكننا مساعدتك؟",
      offline: "نحن خارج أوقات العمل حالياً، لكن يمكنك ترك رسالتك وسنعود إليك في أقرب وقت!"
    }
  }
};

function GulfNeuronWebsite() {
  const [lang, setLang] = useState('en');
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = useState(null);

  const [formErrors, setFormErrors] = useState({});
  const [formValidating, setFormValidating] = useState({}); // New state to track if fields are loading
  const [countryCode, setCountryCode] = useState('+968'); // Default to Oman
  const [openFaq, setOpenFaq] = useState(null);

  const navRef = useRef(null);
  const chatRef = useRef(null); // Add ref for chat widget

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close Mobile Menu
      if (mobileMenuOpen && navRef.current && !navRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
      // Close Chat Widget
      if (chatOpen && chatRef.current && !chatRef.current.contains(event.target) && !event.target.closest('button[data-chat-toggle]')) {
        setChatOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen, chatOpen]);

  // Chatbot state
  const [chatbotStep, setChatbotStep] = useState('welcome'); // welcome, name, email, phone, inquiry, complete
  const [chatbotData, setChatbotData] = useState({ name: '', email: '', phone: '', inquiry: '' });

  const [chatCountryCode, setChatCountryCode] = useState('+968');
  const [chatOptions, setChatOptions] = useState(null);
  const [chatInputValid, setChatInputValid] = useState(null); // null = unknown/empty, true = valid, false = invalid
  const [chatValidating, setChatValidating] = useState(false); // New state to track if chat input is loading validation

  // Ref for auto-scrolling chat messages
  const chatMessagesRef = useRef(null);

  const t = translations[lang];
  const isRTL = lang === 'ar';
  const seo = seoConfig[lang];

  // Initialize EmailJS on component mount
  useEffect(() => {
    initEmailJS();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (chatOpen && chatMessages.length === 0) {
      setChatbotStep('name'); // We are asking for name immediately
      setChatMessages([{ type: 'bot', text: getBotResponse('welcome', lang) }]);
    }
  }, [chatOpen, chatMessages.length, lang]); // Removed t.chat.welcome dependency as we use getBotResponse now

  // Auto-scroll chat messages to bottom
  // Auto-scroll chat messages to bottom
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Real-time Chat Validation Effect
  useEffect(() => {
    if (chatbotStep === 'email') {
      const { valid } = validateEmail(chatInput);
      setChatInputValid(chatInput ? valid : null);
    } else if (chatbotStep === 'phone') {
      // Validate with current country code
      const fullPhone = chatCountryCode ? `${chatCountryCode} ${chatInput}` : chatInput;
      const { valid } = validatePhone(fullPhone);
      // Only show error if they've typed at least a few digits to avoid annoying immediate red X
      setChatInputValid(chatInput.length > 3 ? valid : null);
    } else {
      setChatInputValid(true); // Always valid for other text inputs (name/message - validation happens on send)
    }
  }, [chatInput, chatbotStep, chatCountryCode]);

  const scrollToSection = (section) => {
    setActiveSection(section);
    setMobileMenuOpen(false);
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getBotResponse = (type, lang) => {
    const responses = {
      welcome: {
        en: ["Great! Let's get started. What's your name?", "Hi there! To assist you better, could you please tell me your name?", "Hello! I'd love to help. May I have your name first?"],
        ar: ["مرحباً، ما اسمك؟", "أهلاً بك، ما هو اسمك؟", "مرحباً! هل لي بمعرفة اسمك؟"]
      },
      name_invalid: {
        en: ["Please enter a valid name (at least 3 characters).", "I didn't quite catch that. Could you please enter a valid name?", "Name seems too short. Please try again."],
        ar: ["الرجاء إدخال اسم صحيح.", "لم التقط ذلك. هل يمكنك إرسال اسم صحيح؟", "يبدو الاسم غير صحيح. حاول مرة أخرى."]
      },
      email_ask: {
        en: ["Nice to meet you, {name}! What's your email address?", "Thanks, {name}. Could you share your email so we can reach you?", "Great, {name}. What is the best email to contact you?"],
        ar: ["شكراً {name}، ما بريدك الإلكتروني؟", "{name}، يرجى تزويدنا ببريدك الإلكتروني.", "حسناً {name}، ما هو بريدك الإلكتروني؟"]
      },
      email_invalid: {
        en: ["Please enter a valid email address.", "That doesn't look like a valid email. Please try again.", "I need a correct email address to continue."],
        ar: ["الرجاء إدخال عنوان بريد إلكتروني صحيح.", "لا يبدو هذا بريداً إلكترونياً صحيحاً. يرجى المحاولة مرة أخرى.", "أحتاج إلى بريد إلكتروني صحيح للمتابعة."]
      },
      phone_ask: {
        en: ["Perfect! What's your phone number?", "Got it. May I have your phone number?", "Thanks. Please enter your mobile number."],
        ar: ["ما هو رقم هاتفك؟", "هل يمكننا الحصول على رقم هاتفك؟", "شكراً. الرجاء إدخال رقم هاتفك."]
      },
      phone_invalid: {
        en: ["Please enter a valid phone number.", "That number doesn't seem right. Please check and try again.", "Invalid phone number format."],
        ar: ["الرجاء إدخال رقم هاتف صحيح.", "لا يبدو الرقم صحيحاً. يرجى التحقق والمحاولة مرة أخرى.", "صيغة رقم الهاتف غير صحيحة."]
      },
      inquiry_ask: {
        en: ["Excellent! Please tell me about your AI project or inquiry.", "Great. How can we help you today? Please describe your project.", "Thanks! What would you like to discuss with our AI team?"],
        ar: ["كيف يمكننا مساعدتك؟ يرجى وصف استفسارك.", "يرجى كتابة تفاصيل مشروعك أو طلبك.", "أخبرنا المزيد عن ما تود مناقشته."]
      },
      inquiry_invalid: {
        en: ["Please describe your inquiry in details.", "Please provide a bit more detail (min 20 words).", "I need a little more information to understand your request."],
        ar: ["الرجاء وصف استفسارك في 20 كلمة على الأقل.", "يرج  تقديم مزيد من التفاصيل (20 كلمة كحد أدنى).", "أحتاج إلى مزيد من المعلومات لفهم طلبك."]
      },
      processing: {
        en: ["Thank you! Processing your information...", "One moment while I save your details...", "Sending your inquiry now..."],
        ar: ["شكراً! جاري معالجة معلوماتك...", "لحظة واحدة بينما أحفظ تفاصيلك...", "جاري إرسال استفسارك الآن..."]
      },
      success: {
        en: ["Perfect! We've received your information and will contact you shortly.", "Done! Your inquiry has been sent successfully. We'll be in touch!", "Received! Thank you for reaching out. Expect to hear from us soon."],
        ar: ["تم! لقد استلمنا معلوماتك وسنتواصل معك قريباً.", "تم بنجاح! تم إرسال استفسارك. سنكون على اتصال!", "استلمنا طلبك! شكراً لتواصلك معنا. توقع رداً منا قريباً."]
      },
      error: {
        en: ["Something went wrong. Please try using the contact form.", "I encountered an error. Please try again later or use the form.", "Oops! Connection issue. Please use the contact form below."],
        ar: ["حدث خطأ ما. يرجى محاولة استخدام نموذج التواصل.", "واجهت خطأ. يرجى المحاولة مرة أخرى لاحقاً أو استخدام النموذج.", "عفواً! مشكلة في الاتصال. يرجى استخدام نموذج الاتصال أدناه."]
      },
      restart: {
        en: "Do you want to start a new inquiry?",
        ar: "هل تريد بدء استفسار جديد؟"
      }
    };

    const list = responses[type][lang];
    if (typeof list === 'string') return list;
    return list[Math.floor(Math.random() * list.length)];
  };

  const handleChatSend = async (message = null) => {
    const inputToSend = message || chatInput;
    if (!inputToSend?.trim()) return;

    // Add user message if it wasn't a button click
    if (!message) {
      if (chatbotStep === 'email' && chatInputValid !== true) return; // Strict block
      if (chatbotStep === 'phone' && chatInputValid !== true) return; // Strict block

      // Include country code in the visible chat bubble for phone step
      const displayMsg = chatbotStep === 'phone' ? `${chatCountryCode} ${inputToSend}` : inputToSend;
      setChatMessages(prev => [...prev, { type: 'user', text: displayMsg }]);
      setChatInput('');
    }

    const userInput = inputToSend.trim();
    setChatOptions(null); // Clear options

    // Process based on current step
    setTimeout(async () => {
      let botResponse = '';
      let nextStep = chatbotStep;
      let shouldShowOptions = false;

      // Reset handler
      if (userInput.toLowerCase() === 'start new' || userInput === 'بدء جديد' || userInput.toLowerCase().includes('new inquiry')) {
        setChatbotStep('welcome');
        setChatbotData({ name: '', email: '', phone: '', inquiry: '' });
        const resp = getBotResponse('welcome', lang);
        setChatMessages(prev => [...prev, { type: 'bot', text: resp }]);
        return;
      }
      if (userInput.toLowerCase() === 'close' || userInput === 'إغلاق') {
        setChatOpen(false);
        return;
      }

      switch (chatbotStep) {
        case 'welcome':
          nextStep = 'name'; // We asked for name in previous step, now we process it? No, wait. 


          botResponse = getBotResponse('welcome', lang); // Fallback
          break;

        case 'name': // User just typed their name
          const nameValidation = validateName(userInput);
          if (!nameValidation.valid) {
            botResponse = getBotResponse('name_invalid', lang);
            nextStep = 'name';
          } else {
            setChatbotData(prev => ({ ...prev, name: userInput }));
            botResponse = getBotResponse('email_ask', lang).replace('{name}', userInput);
            nextStep = 'email';
          }
          break;

        case 'email': // User just typed email
          const emailValidation = validateEmail(userInput);
          if (!emailValidation.valid) {
            botResponse = getBotResponse('email_invalid', lang);
            nextStep = 'email';
          } else {
            setChatbotData(prev => ({ ...prev, email: userInput }));
            botResponse = getBotResponse('phone_ask', lang);
            nextStep = 'phone';
          }
          break;

        case 'phone': // User just typed phone
          // Combine with country code if available
          const fullPhone = chatCountryCode ? `${chatCountryCode} ${userInput}` : userInput;
          const phoneValidation = validatePhone(fullPhone);

          if (!phoneValidation.valid) {
            // Try validating without code if user maybe typed it
            const rawValidation = validatePhone(userInput);
            if (rawValidation.valid) {
              setChatbotData(prev => ({ ...prev, phone: rawValidation.formatted }));
              botResponse = getBotResponse('inquiry_ask', lang);
              nextStep = 'inquiry';
            } else {
              botResponse = getBotResponse('phone_invalid', lang);
              nextStep = 'phone';
            }
          } else {
            setChatbotData(prev => ({ ...prev, phone: phoneValidation.formatted }));
            botResponse = getBotResponse('inquiry_ask', lang);
            nextStep = 'inquiry';
          }
          break;

        case 'inquiry': // User just typed inquiry
          const messageValidation = validateMessage(userInput);
          if (!messageValidation.valid) {
            botResponse = getBotResponse('inquiry_invalid', lang);
            nextStep = 'inquiry';
          } else {
            const finalChatbotData = { ...chatbotData, inquiry: userInput };
            setChatbotData(finalChatbotData);

            botResponse = getBotResponse('processing', lang);
            setChatMessages(prev => [...prev, { type: 'bot', text: botResponse }]);

            const result = await sendChatbotEmail(finalChatbotData);

            if (result.success) {
              botResponse = getBotResponse('success', lang);
              nextStep = 'complete';
              shouldShowOptions = true;
            } else {
              botResponse = getBotResponse('error', lang);
              nextStep = 'welcome';
              setChatbotData({ name: '', email: '', phone: '', inquiry: '' });
            }
          }
          break;

        case 'complete': // Conversation ended
          // If user types something here, it's likely a new query or thanks
          if (userInput.length > 0) {
            botResponse = getBotResponse('restart', lang);
            shouldShowOptions = true;
          }
          break;

        default:
          botResponse = getBotResponse('welcome', lang);
          nextStep = 'name';
      }

      setChatbotStep(nextStep);
      // Don't duplicate processing message
      if (nextStep !== 'complete' || !botResponse.includes('Processing')) {
        setChatMessages(prev => [...prev, { type: 'bot', text: botResponse }]);
      }

      if (shouldShowOptions) {
        setChatOptions([
          { label: lang === 'en' ? 'Start New Inquiry' : 'بدء استفسار جديد', value: 'Start New' },
          { label: lang === 'en' ? 'Close Chat' : 'إغلاق المحادثة', value: 'Close' }
        ]);
      }

    }, 600 + Math.random() * 500); // Random delay
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    // Validate all fields
    const countryObj = countryCodes.find(c => c.code === countryCode);
    const firstNameValidation = validateName(formData.firstName, lang);
    const lastNameValidation = validateName(formData.lastName, lang);
    const emailValidation = validateEmail(formData.email, lang);
    const phoneValidation = validatePhone(countryCode + formData.phone, countryObj, lang);
    const messageValidation = validateMessage(formData.message, lang);

    const errors = {};
    if (!firstNameValidation.valid) errors.firstName = firstNameValidation.error;
    if (!lastNameValidation.valid) errors.lastName = lastNameValidation.error;
    if (!emailValidation.valid) errors.email = emailValidation.error;
    if (!phoneValidation.valid) errors.phone = phoneValidation.error;
    if (!messageValidation.valid) errors.message = messageValidation.error;

    // If there are errors, show them
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormStatus('sending');

    // Prepare data with combined name and country code
    const submissionData = {
      ...formData,
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      phone: formatPhoneNumber(formData.phone, countryCode)
    };

    // Send email via EmailJS
    const result = await sendContactEmail(submissionData);

    if (result.success) {
      setFormStatus('success');
      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      setTimeout(() => setFormStatus(null), 5000);
    } else {
      setFormStatus('error');
      setTimeout(() => setFormStatus(null), 5000);
    }
  };

  useEffect(() => {
    if (isRTL) {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [isRTL]);

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white ${isRTL ? 'rtl font-arabic' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Helmet>
        <html lang={lang} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />

        {/* Open Graph */}
        <meta property="og:title" content={seo.ogTitle} />
        <meta property="og:description" content={seo.ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gulfneuron.com" />
        <meta property="og:image" content="https://gulfneuron.com/logo.png" />
        <meta property="og:locale" content={lang === 'en' ? 'en_US' : 'ar_OM'} />

        {/* Twitter Card */}
        <meta name="twitter:card" content={seo.twitterCard} />
        <meta name="twitter:title" content={seo.ogTitle} />
        <meta name="twitter:description" content={seo.ogDescription} />
        <meta name="twitter:image" content="https://gulfneuron.com/logo.png" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        {/* Alternate language versions */}
        <link rel="alternate" hreflang="en" href="https://gulfneuron.com" />
        <link rel="alternate" hreflang="ar" href="https://gulfneuron.com?lang=ar" />
        <link rel="alternate" hreflang="x-default" href="https://gulfneuron.com" />
      </Helmet>

      {/* Header */}
      <header
        ref={navRef}
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled || mobileMenuOpen ? 'bg-slate-950/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}
      >
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 group">
              <div className="relative">
                {/* Logo Replacement: Using an img tag if logo exists, falling back to Brain icon */}
                <img src="/logo.png" alt="Gulf Neuron" className="w-10 h-10 object-contain hidden sm:block" onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'block' }} />
                <Brain className="w-10 h-10 text-cyan-400 group-hover:scale-110 transition-transform duration-300 sm:hidden" style={{ display: 'none' }} />
                {/* Fallback for Brain icon to show if img fails */}
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                {lang === 'ar' ? (
                  <span>
                    <span className="text-cyan-300">جولف</span>&nbsp;<span className="text-blue-400">نيورون</span>
                  </span>
                ) : 'Gulf Neuron'}
              </span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              {Object.entries(t.nav).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => scrollToSection(key)}
                  className={`transition-all duration-300 hover:text-cyan-400 ${activeSection === key ? 'text-cyan-400 font-semibold' : ''}`}
                >
                  {value}
                </button>
              ))}
              <button
                onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 transition-all duration-300"
              >
                <Globe className="w-4 h-4" />
                <span className={lang === 'en' ? 'font-arabic' : ''}>
                  {lang === 'en' ? 'العربية' : 'English'}
                </span>
              </button>
            </div>

            {/* Mobile Actions */}
            <div className="flex items-center gap-3 md:hidden">
              <button
                onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 text-cyan-400 border border-slate-700 hover:border-cyan-500/50 transition-all duration-300"
              >
                <Globe className="w-4 h-4" />
                <span className={`text-sm font-medium ${lang === 'en' ? 'font-arabic' : ''}`}>
                  {lang === 'en' ? 'عربي' : 'EN'}
                </span>
              </button>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-lg border transition-all duration-300 ${mobileMenuOpen ? 'bg-red-500/10 border-red-500/50 text-red-400' : 'bg-slate-800/50 border-slate-700 text-slate-300'}`}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-2 animate-in fade-in slide-in-from-top duration-300 border-t border-slate-800 pt-4">
              {Object.entries(t.nav).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => scrollToSection(key)}
                  className={`w-full ${isRTL ? 'text-right' : 'text-left'} py-3 px-4 rounded-xl transition-all duration-300 flex items-center justify-between ${activeSection === key ? 'bg-cyan-500/10 text-cyan-400 font-bold' : 'hover:bg-slate-800/50 text-slate-300'}`}
                >
                  {value}
                  {activeSection === key && <div className="w-2 h-2 rounded-full bg-cyan-400" />}
                </button>
              ))}
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3lhbiIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center pb-24 md:pb-0">
            <div className="mb-8 inline-block animate-in fade-in slide-in-from-bottom duration-700">
              <div className="flex items-center justify-center gap-2 px-4 py-2 bg-cyan-500/10 rounded-full border border-cyan-500/30">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-400">AI Solutions Provider</span>
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-10 leading-tight animate-in fade-in slide-in-from-bottom duration-700 delay-100">
              {t.hero.title}
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent py-1">
                {t.hero.highlight}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-12 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom duration-700 delay-200">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom duration-700 delay-300">
              <button
                onClick={() => scrollToSection('contact')}
                className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg font-semibold hover:scale-105 transition-all duration-300 shadow-md shadow-cyan-500/20"
              >
                {t.hero.cta}
                <Zap className="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('services')}
                className="px-8 py-4 bg-slate-800/50 backdrop-blur rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 border border-slate-700"
              >
                {t.hero.ctaSecondary}
              </button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 md:bottom-10 left-1/2 -translate-x-1/2 animate-bounce z-10">
          <ChevronDown className="w-8 h-8 text-cyan-400 pointer-events-none" />
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.services.title}</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">{t.services.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {t.services.items.map((service, idx) => {
              const serviceIcons = [Bot, Network, BrainCircuit, Sparkles, Code2, TrendingUp];
              const Icon = serviceIcons[idx] || Cpu;

              return (
                <div
                  key={idx}
                  // Added sticky positioning for the "stacking" effect on mobile
                  // top-24 plus a small index offset ensures they stack nicely with a slight cascade effect if desired, 
                  // or just sticking to the same top helps them fully cover.
                  // giving distinct z-indices ensures proper layering order.
                  className="sticky top-28 md:static group relative p-6 md:p-8 bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/20 overflow-hidden"
                  style={{ zIndex: idx + 1 }} // Ensure stacking order
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-shimmer" />

                  {/* Header: Icon + Title Side-by-Side */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="shrink-0 w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg shadow-cyan-500/30">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold group-hover:text-cyan-400 transition-colors leading-tight">
                      {service.title}
                    </h3>
                  </div>

                  {/* Description Below */}
                  <p className="text-slate-400 leading-relaxed text-lg">
                    {service.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.about.title}</h2>
              <p className="text-xl text-cyan-400">{t.about.subtitle}</p>
            </div>

            <div className="mb-12 p-8 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl border border-cyan-500/30">
              <p className="text-lg text-slate-300 leading-relaxed mb-6">{t.about.content}</p>

              <div className="mt-8 p-6 bg-slate-800/50 rounded-xl">
                <h3 className="text-2xl font-bold mb-3 text-cyan-400">{t.about.mission}</h3>
                <p className="text-slate-300 leading-relaxed">{t.about.missionText}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {/* Stats section removed as requested */}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.faq.title}</h2>
              <p className="text-xl text-slate-300">{t.faq.subtitle}</p>
            </div>

            <div className="space-y-4">
              {t.faq.items.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700 overflow-hidden hover:border-cyan-500/50 transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className={`w-full p-6 ${isRTL ? 'text-right' : 'text-left'} flex items-center justify-between gap-4 hover:bg-slate-800/70 transition-all`}
                  >
                    <span className="font-semibold text-lg">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 text-cyan-400 transition-transform duration-300 flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-6 text-slate-300 animate-in fade-in slide-in-from-top duration-300">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.contact.title}</h2>
              <p className="text-xl text-slate-300">{t.contact.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <ValidatedInput
                      type="text"
                      placeholder={t.contact.form.firstName}
                      value={formData.firstName}
                      onChange={(e) => {
                        setFormData({ ...formData, firstName: e.target.value });
                        if (formErrors.firstName) setFormErrors({ ...formErrors, firstName: null });
                      }}
                      validator={(val) => validateName(val, lang)}
                      onStateChange={(isValid) => { }}
                      onLoading={(loading) => setFormValidating(prev => ({ ...prev, firstName: loading }))}
                      isRTL={isRTL}
                      externalError={formErrors.firstName}
                    />
                  </div>
                  <div>
                    <ValidatedInput
                      type="text"
                      placeholder={t.contact.form.lastName}
                      value={formData.lastName}
                      onChange={(e) => {
                        setFormData({ ...formData, lastName: e.target.value });
                        if (formErrors.lastName) setFormErrors({ ...formErrors, lastName: null });
                      }}
                      validator={(val) => validateName(val, lang)}
                      onStateChange={(isValid) => { }}
                      onLoading={(loading) => setFormValidating(prev => ({ ...prev, lastName: loading }))}
                      isRTL={isRTL}
                      externalError={formErrors.lastName}
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div>
                  <ValidatedInput
                    type="email"
                    placeholder={t.contact.form.email}
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (formErrors.email) setFormErrors({ ...formErrors, email: null });
                    }}
                    validator={(val) => validateEmail(val, lang)}
                    onStateChange={(isValid) => { }}
                    onLoading={(loading) => setFormValidating(prev => ({ ...prev, email: loading }))}
                    isRTL={isRTL}
                    externalError={formErrors.email}
                  />
                </div>

                {/* Phone Field */}
                <div>
                  <ValidatedInput
                    type="tel"
                    prefix={
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="h-full px-3 bg-slate-800/50 border border-slate-700 rounded-l-lg border-r-0 focus:outline-none focus:border-cyan-500 transition-all text-sm appearance-none cursor-pointer hover:bg-slate-700/50"
                        style={{ direction: 'ltr' }}
                      >
                        {countryCodes.map((country) => (
                          <option key={`${country.code}-${country.country}`} value={country.code} className="text-black">
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                    }
                    placeholder={lang === 'en' ? 'Mobile Number' : 'رقم الهاتف'}
                    value={formData.phone}
                    onChange={(e) => {
                      setFormData({ ...formData, phone: e.target.value });
                      if (formErrors.phone) setFormErrors({ ...formErrors, phone: null });
                    }}
                    validator={(val) => {
                      // Find country object
                      const countryObj = countryCodes.find(c => c.code === countryCode);
                      // Validate full phone context
                      return validatePhone(countryCode + val, countryObj, lang);
                    }}
                    onStateChange={(isValid) => { }}
                    onLoading={(loading) => setFormValidating(prev => ({ ...prev, phone: loading }))}
                    dir="ltr" // Force Phone input LTR
                    isRTL={isRTL}
                    externalError={formErrors.phone}
                  />
                </div>

                {/* Message Field */}
                <div>
                  <ValidatedInput
                    isTextArea={true}
                    rows={5}
                    placeholder={t.contact.form.message}
                    value={formData.message}
                    onChange={(e) => {
                      setFormData({ ...formData, message: e.target.value });
                      if (formErrors.message) setFormErrors({ ...formErrors, message: null });
                    }}
                    validator={(val) => validateMessage(val, lang)}
                    onStateChange={(isValid) => { }}
                    onLoading={(loading) => setFormValidating(prev => ({ ...prev, message: loading }))}
                    isRTL={isRTL}
                    externalError={formErrors.message}
                  />
                </div>

                {/* Success Message */}
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    {t.contact.form.success}
                  </div>
                )}

                {/* Error Message */}
                {formStatus === 'error' && (
                  <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    {t.contact.form.error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={formStatus === 'sending' || Object.values(formValidating).some(v => v)}
                  className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg font-semibold hover:scale-105 transition-all duration-300 shadow-md shadow-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {formStatus === 'sending' ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t.contact.form.sending}
                    </span>
                  ) : (
                    t.contact.form.send
                  )}
                </button>
              </form>

              <div className="space-y-6">
                <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                  <h3 className="text-xl font-bold mb-6">{t.contact.info.title}</h3>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <Mail className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-semibold mb-1">{t.contact.info.email}</div>
                        <a href="mailto:gulfneuron@gmail.com" className="text-slate-400 hover:text-cyan-400 transition-colors">
                          gulfneuron@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <MapPin className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-semibold mb-1">{t.contact.info.location}</div>
                        <div className="text-slate-400">{t.contact.info.locationText}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Clock className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                      <div>
                        <div className="font-semibold mb-1">{t.contact.info.hours}</div>
                        <div className="text-slate-400 mb-1">{t.contact.info.hoursText}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl border border-cyan-500/30">
                  <div className="flex items-center gap-3 mb-3">
                    <Sparkles className="w-6 h-6 text-cyan-400" />
                    <h3 className="text-lg font-bold">
                      {lang === 'en' ? 'Ready to innovate?' : 'مستعد للابتكار؟'}
                    </h3>
                  </div>
                  <p className="text-slate-300">
                    {lang === 'en'
                      ? "Let's discuss how AI can transform your business. We're excited to hear about your project!"
                      : "لنناقش كيف يمكن للذكاء الاصطناعي أن يحول عملك. نحن متحمسون لسماع مشروعك!"
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-800">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-8 h-8 text-cyan-400 animate-float" />
                <span className="text-xl font-bold">Gulf Neuron</span>
              </div>
              <p className="text-slate-400">{t.footer.tagline}</p>
            </div>

            <div>
              <h3 className="font-bold mb-4">{t.footer.quickLinks}</h3>
              <div className="space-y-2">
                {Object.entries(t.nav).map(([key, value]) => (
                  <button
                    key={key}
                    onClick={() => scrollToSection(key)}
                    className="block text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-bold mb-4">{t.contact.info.title}</h3>
              <div className="space-y-2 text-slate-400">
                <div>gulfneuron@gmail.com</div>
                <div>{t.contact.info.locationText}</div>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800 text-center text-slate-400">
            © 2025 Gulf Neuron. {t.footer.rights}
          </div>
        </div>
      </footer>

      {/* Chat Widget */}
      <div className={`fixed ${isRTL ? 'left-6' : 'right-6'} bottom-6 z-50`}>
        {chatOpen ? (
          <div ref={chatRef} className="bg-slate-900 rounded-2xl shadow-2xl w-80 md:w-96 border border-slate-700 animate-in fade-in slide-in-from-bottom duration-300">
            <div className="p-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-t-2xl flex items-center justify-between">
              <div>
                <div className="font-bold">{t.chat.title}</div>
                <div className="text-xs text-cyan-100">{t.chat.subtitle}</div>
              </div>
              <button onClick={() => setChatOpen(false)} className="hover:bg-white/20 p-2 rounded-lg transition-all">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={chatMessagesRef} className="h-96 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.type === 'user' ? (isRTL ? 'justify-start' : 'justify-end') : (isRTL ? 'justify-end' : 'justify-start')}`}>
                  <div className={`max-w-[85%] p-3 rounded-lg break-words whitespace-pre-wrap ${msg.type === 'user' ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-300'}`} dir="auto">
                    {msg.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Input Area */}
            <div className="p-4 border-t border-slate-700">
              {/* Options buttons if available */}
              {chatOptions && (
                <div className="flex gap-2 mb-3 flex-wrap">
                  {chatOptions.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleChatSend(opt.value)}
                      className="px-4 py-2 bg-slate-800 border border-cyan-500 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500 hover:text-white transition-all flex-grow md:flex-grow-0"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2 items-center w-full max-w-full">
                {/* Special Inputs for Email and Phone steps */}

                {/* Phone Step: Integrated Country Code Selector + Phone Input */}
                {chatbotStep === 'phone' && (
                  <ValidatedInput
                    type="tel"
                    prefix={
                      <select
                        value={chatCountryCode}
                        onChange={(e) => setChatCountryCode(e.target.value)}
                        className="h-full px-3 bg-slate-800/50 border border-slate-700 rounded-l-lg border-r-0 focus:outline-none focus:border-cyan-500 transition-all text-sm appearance-none cursor-pointer hover:bg-slate-700/50 box-border"
                        style={{ direction: 'ltr' }}
                      >
                        {countryCodes.map((country) => (
                          <option key={`${country.code}-${country.country}`} value={country.code} className="text-black">
                            {country.flag} {country.code}
                          </option>
                        ))}
                      </select>
                    }
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && chatInputValid && !chatValidating && handleChatSend()}
                    placeholder="12345678"
                    className="text-sm py-3"
                    validator={(val) => {
                      const countryObj = countryCodes.find(c => c.code === chatCountryCode);
                      return validatePhone(chatCountryCode + val, countryObj, lang);
                    }}
                    onStateChange={setChatInputValid}
                    onLoading={setChatValidating}
                    dir="ltr"
                    isRTL={false}
                  />
                )}

                {/* Email Step & Other steps */}
                {chatbotStep !== 'phone' && (
                  <ValidatedInput
                    type={chatbotStep === 'email' ? 'email' : 'text'}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && chatInputValid && !chatValidating && handleChatSend()}
                    placeholder={
                      chatbotStep === 'email' ? (lang === 'en' ? 'name@example.com' : 'البريد الإلكتروني') :
                        t.chat.placeholder
                    }
                    className="text-sm py-3"
                    validator={
                      chatbotStep === 'name' ? (val) => validateName(val, lang) :
                        chatbotStep === 'email' ? (val) => validateEmail(val, lang) :
                          chatbotStep === 'inquiry' ? (val) => validateMessage(val, lang) :
                            (val) => ({ valid: val.length > 0 })
                    }
                    onStateChange={setChatInputValid}
                    onLoading={setChatValidating}
                    isRTL={isRTL}
                  />
                )}

                <button
                  onClick={() => chatInputValid && !chatValidating && handleChatSend()}
                  disabled={!chatInputValid || chatValidating}
                  className="p-2 bg-cyan-500 hover:bg-cyan-600 rounded-lg transition-all flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setChatOpen(true)}
            data-chat-toggle="true"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 rounded-full shadow-lg hover:scale-105 transition-all duration-300 group"
          >
            <MessageCircle className="w-6 h-6 group-hover:rotate-12 transition-transform" />
          </button>
        )}
      </div>
    </div>
  );
}

export default GulfNeuronWebsite;